import { gql, useQuery } from '@apollo/client'
import { mutationNicoxApi } from '@/app/actions'
import { useState } from 'react'
import { useSession } from 'next-auth/react'

export const createMultipartFormData = ({ query, variables }) => {
  const formData = new FormData()
  const map = {}
  let i = 0

  for (const [key, value] of Object.entries(variables)) {
    if (Array.isArray(value)) {
      const allBlobs = value.every((file) => file instanceof Blob)
      if (allBlobs) {
        value.forEach((file, index) => {
          formData.append(`${key}.${index}`, file)
          map[i] = [`variables.${key}.${index}`]
          i++
        })
      }
    } else if (value instanceof Blob) {
      formData.append(key, value)
      map[i] = [`variables.${key}`]
      i++
    }
  }

  const operations = {
    query,
    variables: variables || {}
  }

  const multipartFormData = new FormData()
  multipartFormData.append('operations', JSON.stringify(operations))
  multipartFormData.append('map', JSON.stringify(map))

  i = 0
  for (const [, value] of formData.entries()) {
    if (value instanceof Blob) {
      multipartFormData.append(i, value)
      i++
    }
  }

  return multipartFormData
}

export const GET_REASONS = gql`
  query getReasons {
    refundReasons(undelivered_by_van: true) {
      id
      name
    }
  }
`

export const useReasons = () => {
  const { data, loading, error } = useQuery(GET_REASONS, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all'
  })

  return { data, loading, error }
}

export const GET_REASONS_MANUAL_SIGNATURE = gql`
  query getManualReasons {
    reasons(manual_signature: true) {
      codigo
      nombre
    }
  }
`

export const useManualReasons = () => {
  const { data, loading, error } = useQuery(GET_REASONS_MANUAL_SIGNATURE, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all'
  })

  return { data, loading, error }
}
export const GET_REASONS_FOR_VALIDATION = gql`
  query Reasons {
    reasons(is_presence_validation: true) {
      codigo
      nombre
    }
  }
`

export const useReasonsForValidation = () => {
  const { data, loading, error } = useQuery(GET_REASONS_FOR_VALIDATION, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all'
  })

  return { data, loading, error }
}

export const POST_DELIVER_ORDER = {
  query: `
    mutation deliverOrder(
      $order_id: Int!
      $digital_signature: String
      $printed_name: String
      $customer_dni: String
      $observations: String
      $latitude: Float
      $longitude: Float
    ) {
      setDeliveryStatus(
        status: "delivered"
        order_id: $order_id
        latitude: $latitude
        longitude: $longitude
        customer_dni: $customer_dni
        printed_name: $printed_name
        observations: $observations
        digital_signature: $digital_signature
        reason: 0
      ) {
        msg
      }
    }
  `
}

export const useDeliverOrder = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { data: session } = useSession()

  const deliverOrder = async (variables) => {
    setLoading(true)
    setError(null)
    try {
      const result = await mutationNicoxApi({
        userToken: session.otpToken,
        queryObject: POST_DELIVER_ORDER,
        variables
      })
      setData(result)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }
  return { deliverOrder, data, loading, error }
}

const POST_DELIVER_ORDER_ON_PAPER = {
  query: `
      mutation deliverOrder(
      $order_id: Int!
      $image: String
      $image_2: String
      $image_3: String
      $printed_name: String
      $customer_dni: String
      $observations: String
      $reason: Int
      $latitude: Float
      $longitude: Float
    ) {
      setDeliveryStatus(
        status: "delivered"
        order_id: $order_id
        latitude: $latitude
        longitude: $longitude
        customer_dni: $customer_dni
        printed_name: $printed_name
        observations: $observations
        image: $image
        image_2: $image_2
        image_3: $image_3
        reason: $reason
      ) {
        msg
      }
    }
  `
}
export const useDeliverOrderOnPaper = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { data: session } = useSession()

  const deliverOrderOnPaper = async (variables) => {
    setLoading(true)
    setError(null)
    try {
      const result = await mutationNicoxApi({
        userToken: session.otpToken,
        queryObject: POST_DELIVER_ORDER_ON_PAPER,
        variables
      })
      setData(result)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  return { deliverOrderOnPaper, data, loading, error }
}

const POST_NOT_DELIVERY_ORDER = {
  query: `
  mutation deliverOrder(
    $order_id: Int!
    $files: [String]
    $observations: String
    $reason: Int
    $latitude: Float
    $longitude: Float
  ) {
    setDeliveryStatus(
      status: "undelivered"
      order_id: $order_id
      latitude: $latitude
      longitude: $longitude
      observations: $observations
      files: $files
      reason: $reason
    ) {
      msg
    }
  }
  `
}

const SAVE_DELIVERY_STATUS_IMAGE = {
  query: `
  mutation SaveDeliveryStatusImage(
    $order_id: Int!
    $files: [String]!
  ) {
    saveDeliveryStatusImage(
      order_id: $order_id
      files: $files
    ) {
      msg
      error
    }
  }
  `
}

export const useSaveDeliveryImage = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { data: session } = useSession()

  const saveDeliveryImage = async (variables) => {
    setLoading(true)
    setError(null)
    try {
      const result = await mutationNicoxApi({
        userToken: session.otpToken,
        queryObject: SAVE_DELIVERY_STATUS_IMAGE,
        variables
      })
      setData(result)
      return result
    } catch (err) {
      setError(err)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return { saveDeliveryImage, data, loading, error }
}

export const useUndeliveredOrder = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { data: session } = useSession()

  const undeliveredOrder = async (variables) => {
    setLoading(true)
    setError(null)
    try {
      const result = await mutationNicoxApi({
        userToken: session.otpToken,
        queryObject: POST_NOT_DELIVERY_ORDER,
        variables
      })
      setData(result)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  return { undeliveredOrder, data, loading, error }
}

export const POST_POSTPONED_ORDER = {
  query: ` mutation postponeOrder($order_id: Int!) {
    setDeliveryStatus(order_id: $order_id, status: "postponed") {
      error
      msg
    }
  }`
}

export const usePostPostponedOrder = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { data: session } = useSession()

  const postPostponedOrder = async (variables) => {
    setLoading(true)
    setError(null)
    try {
      const result = await mutationNicoxApi({
        userToken: session.otpToken,
        queryObject: POST_POSTPONED_ORDER,
        variables
      })
      setData(result)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  return { postPostponedOrder, data, loading, error }
}

export const POST_PENDING_ORDER = {
  query: `
    mutation pendingOrder($order_id: Int!) {
      setDeliveryStatus(order_id: $order_id, status: "pending") {
        error
        msg
      }
    }
  `
}
export const usePostPendingOrder = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { data: session } = useSession()

  const postPendingOrder = async (variables) => {
    setLoading(true)
    setError(null)
    try {
      const result = await mutationNicoxApi({
        userToken: session.otpToken,
        queryObject: POST_PENDING_ORDER,
        variables
      })
      setData(result)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  return { postPendingOrder, data, loading, error }
}
